import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Loader2, RefreshCw, X } from "lucide-react";
import { AppShell } from "@/components/app-shell/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel, PanelHeader } from "@/components/common/Card";
import { OutcomeBadge } from "@/components/common/OutcomeBadge";
import { api } from "@/lib/api";
import type { EventRow } from "@/lib/types";
import { OUTCOMES, type EventOutcome } from "@/lib/outcomes";
import { fmtTs, fmtTsShort, relativeTime } from "@/lib/format";

export const Route = createFileRoute("/events")({
  component: EventsPage,
});

function EventsPage() {
  const [outcomeFilter, setOutcomeFilter] = useState<EventOutcome | "all">("all");
  const [selected, setSelected] = useState<EventRow | null>(null);

  const recentQ = useQuery({
    queryKey: ["events", 500],
    queryFn: () => api.recent(500),
    refetchInterval: 15_000,
  });
  const statsQ = useQuery({ queryKey: ["stats", 1], queryFn: () => api.stats(1) });

  const rows = recentQ.data?.results ?? [];

  const filtered = useMemo(
    () => (outcomeFilter === "all" ? rows : rows.filter((r) => r.outcome === outcomeFilter)),
    [rows, outcomeFilter],
  );

  // Per-outcome counts (last 24h via filtered set's full range).
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(r.outcome, (m.get(r.outcome) ?? 0) + 1);
    return m;
  }, [rows]);

  // 24-bucket hourly chart from stats (which is grouped per day, so we fall
  // back to bucketing the last-500 stream by hour instead).
  const buckets = useMemo(() => {
    const b = Array.from({ length: 24 }, () => 0);
    const cutoff = Date.now() / 1000 - 24 * 3600;
    for (const r of rows) {
      if (r.ts < cutoff) continue;
      const h = new Date(r.ts * 1000).getUTCHours();
      b[h]++;
    }
    return b;
  }, [rows]);
  const maxBucket = Math.max(1, ...buckets);

  // 7-day deliverability for context.
  const statTotals = (statsQ.data?.results ?? []).reduce(
    (acc, r) => {
      acc.total += r.n;
      if (r.outcome === "sent") acc.sent += r.n;
      return acc;
    },
    { total: 0, sent: 0 },
  );

  return (
    <AppShell title="Live events">
      <PageHeader
        eyebrow="events · live"
        title="Live events"
        description="Every send attempt across every form, decorated with the security signals that decided its fate. Auto-refreshes every 15 seconds."
        actions={
          <button
            type="button"
            onClick={() => recentQ.refetch()}
            disabled={recentQ.isFetching}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-surface/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:bg-surface disabled:opacity-50"
          >
            <RefreshCw
              className={`h-3 w-3 ${recentQ.isFetching ? "animate-spin" : ""}`}
            />
            refresh
          </button>
        }
      />

      <div className="mb-6 grid grid-cols-12 gap-6">
        <Panel className="col-span-12 p-6 lg:col-span-8">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-display text-sm font-semibold text-zinc-100">
                Hourly volume
              </h3>
              <p className="mt-0.5 text-[11px] text-zinc-500">
                Last 24 hours · from the most recent {rows.length} events
              </p>
            </div>
          </div>
          <div className="flex h-40 items-end justify-between gap-1.5">
            {buckets.map((n, i) => (
              <div
                key={i}
                className="flex-1 rounded-t-sm bg-zinc-700/40 transition-colors hover:bg-ember/60"
                style={{ height: `${(n / maxBucket) * 100 || 2}%` }}
                title={`${String(i).padStart(2, "0")}:00 · ${n} events`}
              />
            ))}
          </div>
        </Panel>

        <Panel className="col-span-12 p-6 lg:col-span-4">
          <h3 className="mb-4 font-display text-sm font-semibold text-zinc-100">
            7-day deliverability
          </h3>
          <div className="font-display text-4xl font-semibold text-zinc-100">
            {statTotals.total
              ? `${((statTotals.sent / statTotals.total) * 100).toFixed(1)}%`
              : "—"}
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {statTotals.sent.toLocaleString()} sent ·{" "}
            {(statTotals.total - statTotals.sent).toLocaleString()} blocked
          </p>
        </Panel>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <FilterChip
          active={outcomeFilter === "all"}
          onClick={() => setOutcomeFilter("all")}
        >
          all · {rows.length}
        </FilterChip>
        {OUTCOMES.map((o) => (
          <FilterChip
            key={o}
            active={outcomeFilter === o}
            onClick={() => setOutcomeFilter(o)}
          >
            {o} · {counts.get(o) ?? 0}
          </FilterChip>
        ))}
      </div>

      <Panel className="overflow-hidden">
        <PanelHeader
          title="Event stream"
          subtitle={
            recentQ.isLoading
              ? "loading…"
              : `${filtered.length} of ${rows.length} events`
          }
        />
        <div className="overflow-x-auto">
          {recentQ.isLoading ? (
            <div className="flex items-center gap-2 px-6 py-8 text-xs text-zinc-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> loading events…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-12 text-center text-xs text-zinc-500">
              No events match this filter.
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-6 py-3 font-medium">Time</th>
                  <th className="px-6 py-3 font-medium">Form key</th>
                  <th className="px-6 py-3 font-medium">Outcome</th>
                  <th className="px-6 py-3 font-medium">Geo</th>
                  <th className="px-6 py-3 font-medium">ASN</th>
                  <th className="px-6 py-3 font-medium">Spam</th>
                  <th className="px-6 py-3 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {filtered.map((e, i) => (
                  <tr
                    key={`${e.ts}-${i}`}
                    onClick={() => setSelected(e)}
                    className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.03]"
                  >
                    <td className="px-6 py-3 text-zinc-500" title={fmtTs(e.ts)}>
                      {fmtTsShort(e.ts)}
                    </td>
                    <td className="px-6 py-3 text-zinc-200">
                      {e.site_key.slice(0, 16)}…
                    </td>
                    <td className="px-6 py-3">
                      <OutcomeBadge outcome={e.outcome} />
                    </td>
                    <td className="px-6 py-3 text-zinc-400">{e.country ?? "—"}</td>
                    <td className="px-6 py-3 text-zinc-500">
                      {e.asn ? `AS${e.asn}` : "—"}
                    </td>
                    <td className="px-6 py-3 text-zinc-400">
                      {e.spam_score ?? "—"}
                    </td>
                    <td className="px-6 py-3 text-zinc-400">{e.reason ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Panel>

      {selected && <EventDetail event={selected} onClose={() => setSelected(null)} />}
    </AppShell>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
        active
          ? "bg-surface text-zinc-100 ring-1 ring-white/5"
          : "text-zinc-500 hover:text-zinc-200"
      }`}
    >
      {children}
    </button>
  );
}

function EventDetail({ event, onClose }: { event: EventRow; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="fade-up flex h-full w-full max-w-md flex-col border-l border-border bg-background"
      >
        <div className="flex items-start justify-between border-b border-white/5 px-6 py-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              event detail
            </div>
            <h3 className="mt-1 font-display text-lg font-semibold text-zinc-100">
              {event.outcome}
            </h3>
            <p className="mt-0.5 text-[11px] text-zinc-500">{relativeTime(event.ts)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-400 hover:bg-surface hover:text-zinc-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <dl className="space-y-4 text-xs">
            <Row label="Timestamp" value={fmtTs(event.ts)} mono />
            <Row label="Form key" value={event.site_key} mono />
            <Row label="Outcome" value={event.outcome} mono />
            <Row label="Reason" value={event.reason ?? "—"} />
            <Row label="Country" value={event.country ?? "—"} />
            <Row label="ASN" value={event.asn ? `AS${event.asn}` : "—"} mono />
            <Row label="Spam score" value={String(event.spam_score ?? "—")} mono />
            <Row label="IP hash" value={event.ip_hash} mono />
          </dl>
        </div>
      </aside>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="mb-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </dt>
      <dd
        className={`break-all text-zinc-200 ${mono ? "font-mono text-[11px]" : "text-sm"}`}
      >
        {value}
      </dd>
    </div>
  );
}
